import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import Close from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

const LANGUAGE_LABELS = {
  'en-US': 'English',
  'fr-CA': 'Français',
};

const initials = name =>
  name
    .split(' ')
    .map(part => part[0])
    .join('')
    .slice(0, 2);

const CaptionRow = ({ caption, partial }) => (
  <div
    className={`live-captions-panel__row${partial ? ' is-partial' : ''}`}
    data-caption-state={partial ? 'partial' : 'final'}
  >
    <Avatar
      alt={caption.speaker.name}
      src={caption.speaker.avatarUrl}
      sx={{ height: 32, width: 32 }}
    >
      {caption.speaker.avatarFallback || initials(caption.speaker.name)}
    </Avatar>
    <span>{caption.text}</span>
  </div>
);

CaptionRow.propTypes = {
  caption: PropTypes.shape({
    text: PropTypes.string.isRequired,
    speaker: PropTypes.shape({
      name: PropTypes.string.isRequired,
      avatarUrl: PropTypes.string,
      avatarFallback: PropTypes.string,
    }).isRequired,
  }).isRequired,
  partial: PropTypes.bool.isRequired,
};

const LiveCaptionsPanel = ({ captionState, languageCode, onClose, t }) => {
  const listRef = useRef(null);
  const scrollbarTimerRef = useRef(null);
  const autoFollowingRef = useRef(true);
  const [autoFollowing, setAutoFollowing] = useState(true);
  const [userScrolling, setUserScrolling] = useState(false);
  const partialCaptions = Object.values(captionState.partialCaptionsByStream);
  const languageLabel = LANGUAGE_LABELS[languageCode];

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || !autoFollowingRef.current) return;
    list.scrollTop = list.scrollHeight;
  }, [captionState.finalCaptions, captionState.partialCaptionsByStream]);

  useEffect(
    () => () => {
      if (scrollbarTimerRef.current) {
        clearTimeout(scrollbarTimerRef.current);
      }
    },
    [],
  );

  const jumpToNewest = () => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
    autoFollowingRef.current = true;
    setAutoFollowing(true);
    setUserScrolling(false);
  };

  const onScroll = event => {
    const element = event.currentTarget;
    const distanceFromBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight;
    const atBottom = distanceFromBottom <= 24;

    autoFollowingRef.current = atBottom;
    setAutoFollowing(atBottom);
    if (atBottom) {
      setUserScrolling(false);
      return;
    }

    setUserScrolling(true);
    if (scrollbarTimerRef.current) {
      clearTimeout(scrollbarTimerRef.current);
    }
    scrollbarTimerRef.current = setTimeout(() => setUserScrolling(false), 800);
  };

  return (
    <section
      aria-label={t('liveCaptions.panelTitle')}
      className="live-captions-panel"
    >
      <header className="live-captions-panel__header">
        <div>
          <div className="live-captions-panel__title">
            <h2>{t('liveCaptions.panelTitle')}</h2>
            {languageLabel && (
              <span className="live-captions-panel__language">
                {languageLabel}
              </span>
            )}
          </div>
          <p>{t('liveCaptions.panelDescription')}</p>
        </div>
        <IconButton
          aria-label={t('preCall.close')}
          onClick={onClose}
          size="small"
        >
          <Close />
        </IconButton>
      </header>
      <div
        aria-live="polite"
        className={`live-captions-panel__list${
          userScrolling ? ' is-scrolling' : ''
        }`}
        data-testid="live-captions-list"
        onScroll={onScroll}
        ref={listRef}
        role="log"
      >
        {captionState.finalCaptions.map(caption => (
          <CaptionRow caption={caption} key={caption.id} partial={false} />
        ))}
        {partialCaptions.map(caption => (
          <CaptionRow caption={caption} key={caption.id} partial />
        ))}
      </div>
      {!autoFollowing && (
        <Button
          className="live-captions-panel__jump"
          onClick={jumpToNewest}
          size="small"
          startIcon={<ArrowDownward />}
          variant="outlined"
        >
          {t('liveCaptions.jumpToNewest')}
        </Button>
      )}
    </section>
  );
};

const captionShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  speaker: PropTypes.shape({
    name: PropTypes.string.isRequired,
    avatarUrl: PropTypes.string,
    avatarFallback: PropTypes.string,
  }).isRequired,
});

LiveCaptionsPanel.propTypes = {
  captionState: PropTypes.shape({
    finalCaptions: PropTypes.arrayOf(captionShape).isRequired,
    partialCaptionsByStream: PropTypes.objectOf(captionShape).isRequired,
  }).isRequired,
  languageCode: PropTypes.oneOf(['en-US', 'fr-CA']),
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default LiveCaptionsPanel;
